import React, { useState, useRef } from 'react';
import { makeStyles } from 'tss-react/mui';
import VideoParticipantTile from './VideoParticipantTile'


const useStyles = makeStyles()((theme, _params, classes) => ({
  webcamsContainer: {
    display: 'flex',
    // Single row that never wraps: tiles are height-locked (100% + 4:3), so as
    // the panel is made taller each tile grows and stays as wide as it needs to
    // be. Rather than wrapping to a second row (which has nowhere to go
    // vertically), overflow spills off the right edge and scrolls horizontally.
    flexWrap: 'nowrap',
    alignItems: 'flex-start',
    gap: theme.spacing(1.5),
    padding: theme.spacing(1.5),
    // Video area surface — shared with the docked tiles (black on midnight).
    backgroundColor: theme.palette.custom.videoAreaBg,
    height: '100%',
    overflowX: 'auto',
    overflowY: 'hidden',
    boxSizing: 'border-box',
  },
  webcamContainer: {
    position: 'relative',
    height: '100%',
    width: 'auto',
    // Never let flexbox squeeze a tile below its aspect-derived width — extra
    // tiles overflow the row and the container scrolls horizontally instead.
    flexShrink: 0,
    // The whole panel is the aspect-locked, drag-to-resize unit (grab the
    // bottom-right corner). Keeping resize on the OUTER container — not the
    // inner video box — means the panel and the video scale together as one.
    // width: 280,
    aspectRatio: '4 / 3',
    minWidth: 180,
    // No maxWidth: a tall panel yields a wide 4:3 tile; capping it at the
    // container width would flatten the aspect ratio. Let it grow and overflow.
    // resize: 'both',
    backgroundColor: theme.palette.custom.videoTileBg,
    borderRadius: theme.shape.borderRadius,
    overflow: 'hidden',
    border: `1px solid ${theme.palette.custom.line}`,
    transition: 'box-shadow 0.2s ease',
    '&:hover': {
      boxShadow: '0 4px 12px rgba(0, 0, 0, 0.4)',
    },
  },
  resizable: {
    resize: 'both',
  },
  // Drop target being hovered during a tile drag — the dragged tile will be
  // inserted at this tile's slot. A left-edge accent bar marks the insertion
  // point without shifting layout.
  dropTarget: {
    boxShadow: `inset 3px 0 0 0 ${theme.palette.primary.main}`,
  },
  // The tile currently being dragged — dimmed so the drag is legible.
  dragging: {
    opacity: 0.4,
  },
  // Drag handle is the header bar only, so dragging the tile doesn't fight the
  // native <video> controls (volume slider / scrubber) inside the video box.
  dragHandle: {
    cursor: 'grab',
    '&:active': {
      cursor: 'grabbing',
    },
  },
  webcamHeader: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    backgroundColor: theme.palette.custom.videoTileBg,
    padding: theme.spacing(0.5, 0.75),
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: theme.spacing(0.5),
    opacity: 0,
    transition: 'opacity 0.2s ease',
    zIndex: 2,
  },
  // Reveal the header (Private button, name, controls) on hover of the tile.
  webcamContainerHovered: {
    [`&:hover .${classes.webcamHeader}`]: {
      opacity: 1,
    },
  },
  webcamHeaderLeft: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(0.75),
    minWidth: 0, // let the username truncate instead of pushing controls off-screen
    flex: 1,
  },
  webcamHeaderRight: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(0.5),
    flexShrink: 0,
  },
  username: {
    color: theme.palette.text.primary,
    fontSize: '0.875rem',
    fontWeight: 500,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    minWidth: 0,
  },
  headerButton: {
    padding: theme.spacing(0.25, 1),
    minWidth: 'auto',
    fontSize: '0.7rem',
    lineHeight: 1.4,
    flexShrink: 0,
    '&.privateBtn': {
      backgroundColor: theme.palette.primary.main,
      color: theme.palette.custom.accentContrastText,
      '&:hover': {
        backgroundColor: theme.palette.primary.dark,
      },
    },
  },
  headerIcon: {
    padding: 4,
    color: theme.palette.text.secondary,
    '&:hover': {
      color: theme.palette.text.primary,
      backgroundColor: 'rgba(255, 255, 255, 0.1)',
    },
  },
  qualitySelect: {
    fontSize: '0.7rem',
    color: theme.palette.text.primary,
    backgroundColor: 'rgba(255, 255, 255, 0.08)',
    borderRadius: 4,
    padding: '0 4px',
    '&:before, &:after': { display: 'none' },
    '& .MuiSelect-select': { paddingRight: 18 },
    '& .MuiSelect-icon': { color: theme.palette.text.secondary },
  },
  webcamSwfContainer: {
    // Fills the resizable outer container completely, so the video scales 1:1
    // with the panel as it's resized. Sizing/aspect/resize all live on the
    // outer .webcamContainer now.
    position: 'absolute',
    inset: 0,
    backgroundColor: '#000000',
  },
  video: {
    width: '100%',
    height: '100%',
    objectFit: 'cover',
  },
  placeholder: {
    width: '100%',
    height: '100%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: theme.palette.text.secondary,
    fontSize: '0.875rem',
    backgroundImage: `linear-gradient(135deg, ${theme.palette.custom.videoTileBg} 0%, ${theme.palette.custom.panel} 100%)`,
  },
  // Covers the (still-mounted) <video> when the publisher pauses their cam, so
  // the tile shows "camera paused" rather than a frozen last frame. The video
  // element stays mounted underneath so its bundled AUDIO keeps playing.
  pausedOverlay: {
    position: 'absolute',
    inset: 0,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: theme.palette.text.secondary,
    fontSize: '0.875rem',
    backgroundImage: `linear-gradient(135deg, ${theme.palette.custom.videoTileBg} 0%, ${theme.palette.custom.panel} 100%)`,
    zIndex: 1,
  },
  speaks: {
    position: 'absolute',
    // Top-left, not bottom — the native <video> controls bar occupies the
    // bottom on hover; keeping the voice-activity meter up top avoids overlap.
    top: theme.spacing(1),
    left: theme.spacing(1),
    width: 40,
    height: 6,
    backgroundColor: 'rgba(0, 0, 0, 0.6)',
    borderRadius: 3,
    overflow: 'hidden',
    zIndex: 2,
    pointerEvents: 'none', // never intercept clicks meant for the video controls
  },
  volume: {
    height: '100%',
    backgroundColor: theme.palette.secondary.main,
    width: '0%',
    transition: 'width 0.1s ease',
    '&.speaking': {
      width: '80%',
    },
  },
}));

const VideoGrid = ({ participants = [], onCloseWebcam, onWindowOut, onPrivateRequest, onViewQualityChange, onReorder, poppedOutUsers, statsOpenUsers = [], statsAnchors = {}, statsPositions = {}, onToggleStats, onCloseStats, onStatsPositionChange, onOpenVolume, noodledUserId = null, noodleTrack = null }) => {
  const { classes } = useStyles();
  // Id of the tile currently being dragged (for dimming) and the id of the tile
  // being hovered over as a drop target (for the insertion accent). draggedIdRef
  // mirrors draggedId so the drop handler reads it synchronously (dataTransfer
  // payloads are unreliable across some browsers / same-origin quirks).
  const [draggedId, setDraggedId] = useState(null);
  const [dropTargetId, setDropTargetId] = useState(null);
  const draggedIdRef = useRef(null);
  // Per-participant desired view quality (low|default|high). Capped server-side
  // at the publisher's output quality — see mediasoupService quality model.
  const [viewQuality, setViewQuality] = useState({});

  const handleQualityChange = (participantId, quality) => {
    setViewQuality((prev) => ({ ...prev, [participantId]: quality }));
    if (onViewQualityChange) onViewQualityChange(participantId, quality);
  };

  const displayParticipants = participants;

  const handleDragStart = (e, participant) => {
    draggedIdRef.current = participant.id;
    setDraggedId(participant.id);
    e.dataTransfer.effectAllowed = 'move';
    // Firefox requires data to be set for a drag to initiate at all.
    try { e.dataTransfer.setData('text/plain', participant.id); } catch (_) {}
  };

  const handleDragEnd = () => {
    draggedIdRef.current = null;
    setDraggedId(null);
    setDropTargetId(null);
  };

  // A tile is a drop target: allow the drop and mark it as the insertion slot.
  const handleTileDragOver = (e, participant) => {
    if (!draggedIdRef.current || draggedIdRef.current === participant.id) return;
    e.preventDefault(); // required to allow a drop
    e.dataTransfer.dropEffect = 'move';
    if (dropTargetId !== participant.id) setDropTargetId(participant.id);
  };

  const handleTileDrop = (e, participant) => {
    e.preventDefault();
    const sourceId = draggedIdRef.current;
    setDropTargetId(null);
    if (sourceId && sourceId !== participant.id && onReorder) {
      onReorder(sourceId, participant.id);
    }
  };

  const handleCloseWebcam = (participantId) => {
    if (onCloseWebcam) onCloseWebcam(participantId);
  };

  const handleWindowOut = (participantId) => {
    if (onWindowOut) onWindowOut(participantId);
  };

  const handlePrivateRequest = (participantId) => {
    if (onPrivateRequest) onPrivateRequest(participantId);
  };

  return (
    <div className={classes.webcamsContainer}>
      {displayParticipants.map((participant) => (
        !poppedOutUsers.includes(participant.id) && ((
        <VideoParticipantTile
          key={participant.id}
          participant={participant}
          classes={classes}
          docked
          noodled={!!noodledUserId && participant.id === noodledUserId}
          noodleTrack={
            noodleTrack && noodleTrack.userId === participant.id ? noodleTrack : null
          }
          viewQuality={viewQuality[participant.id] || 'default'}
          isDragging={draggedId === participant.id}
          isDropTarget={dropTargetId === participant.id}
          onDragStart={handleDragStart}
          onDragEnd={handleDragEnd}
          onDragOver={handleTileDragOver}
          onDrop={handleTileDrop}
          onDragLeave={() => setDropTargetId((cur) => (cur === participant.id ? null : cur))}
          onPrivateRequest={handlePrivateRequest}
          onQualityChange={handleQualityChange}
          onWindowOut={handleWindowOut}
          onClose={handleCloseWebcam}
          statsOpen={statsOpenUsers.includes(participant.id)}
          statsAnchor={statsAnchors[participant.id]}
          statsPos={statsPositions[participant.id]}
          onToggleStats={onToggleStats}
          onCloseStats={onCloseStats}
          onStatsPositionChange={onStatsPositionChange}
          onOpenVolume={onOpenVolume}
          remoteVolume={participant.remoteVolume}
        />
          ))
      ))}

      {/* Empty placeholder if no participants */}
      {displayParticipants.length === 0 && (
        <div className={classes.placeholder} style={{ 
          width: '100%', 
          height: '100%',
          minHeight: 40,
          fontSize: '0.75rem',
          padding: '8px'
        }}>
          Click the 👁️ watch button next to a user to view their webcam
        </div>
      )}
    </div>
  );
};

export default VideoGrid;